import { AxiosError } from "axios";

import {
  useApply,
  useMyApplicationForShift,
  useWithdraw,
} from "@/features/applications/model/useApplications";
import { Badge, Button } from "@/shared/ui";

interface Props {
  shiftId: string;
  shiftStatus: string;
}

export function ApplyButton({ shiftId, shiftStatus }: Props) {
  const { data: myApp } = useMyApplicationForShift(shiftId);
  const apply = useApply();
  const withdraw = useWithdraw();

  const shiftNotOpen = shiftStatus !== "OPEN";

  if (myApp && myApp.status === "CONFIRMED") {
    return (
      <Badge variant="success" dot>
        You're confirmed for this shift
      </Badge>
    );
  }

  if (myApp && myApp.status === "PENDING") {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <Badge variant="warning" dot>
          Application pending
        </Badge>
        <Button
          variant="outline"
          size="sm"
          onClick={() => withdraw.mutate(myApp.id)}
          disabled={withdraw.isPending}
        >
          {withdraw.isPending ? "Withdrawing…" : "Withdraw"}
        </Button>
      </div>
    );
  }

  if (shiftNotOpen) {
    return (
      <span className="text-sm text-copy-muted">
        Shift is {shiftStatus.toLowerCase()}
      </span>
    );
  }

  const errMsg =
    apply.error instanceof AxiosError
      ? (apply.error.response?.data?.detail ?? "Could not apply")
      : null;

  return (
    <div className="space-y-2">
      <Button onClick={() => apply.mutate(shiftId)} disabled={apply.isPending}>
        {apply.isPending ? "Applying…" : "Apply for this shift"}
      </Button>
      {errMsg && <p className="text-sm text-destructive">{errMsg}</p>}
    </div>
  );
}
